import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { api } from '../services/api';
import {
  Box, Container, Card, CardContent, Button, Typography, CircularProgress,
  Alert, Checkbox, Paper, Divider, Chip, TextField, FormControlLabel, Stack, MenuItem
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Person as PersonIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';

function Enrollment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [program, setProgram] = useState(null);
  const [children, setChildren] = useState([]);
  const [enrolledChildIds, setEnrolledChildIds] = useState([]);
  const [pendingChildIds, setPendingChildIds] = useState([]);
  const [existingCompletedCount, setExistingCompletedCount] = useState(0);
  const [selectedChildren, setSelectedChildren] = useState([]);
  const [customFieldData, setCustomFieldData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const [programData, childrenData] = await Promise.all([
        api.getProgram(id),
        api.getChildren()
      ]);
      setProgram(programData);
      setChildren(childrenData || []);
      try {
        const mine = await api.getEnrollments();
        const forProgram = (mine || []).filter((e) => e.program_id === id);
        const completed = forProgram.filter((e) => ['completed', 'paid'].includes(e.status?.toLowerCase()));
        setEnrolledChildIds(completed.map((e) => e.enrollee_id));
        setPendingChildIds(forProgram.filter((e) => e.status?.toLowerCase() === 'pending').map((e) => e.enrollee_id));
        setExistingCompletedCount(completed.length);
      } catch (err) { console.warn('Could not fetch existing enrollments:', err); }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load enrollment information.');
    } finally {
      setLoading(false);
    }
  };

  const hasCustomFields = program?.feature_custom_fields && program?.custom_fields?.length > 0;

  const isChildEligible = (child) => {
    const g = child.child_properties?.grade;
    if (g !== null && g !== undefined) {
      if (program.min_grade !== null && program.min_grade && g < program.min_grade) return false;
      if (program.max_grade !== null && program.max_grade && g > program.max_grade) return false;
    }
    return true;
  };

  const toggleChild = (childId) =>
    setSelectedChildren((prev) => prev.includes(childId) ? prev.filter((c) => c !== childId) : [...prev, childId]);

  const setCustom = (childId, fieldId, value) =>
    setCustomFieldData((prev) => ({ ...prev, [childId]: { ...prev[childId], [fieldId]: value } }));

  const validateCustom = () => {
    if (!hasCustomFields) return true;
    for (const childId of selectedChildren) {
      for (const field of program.custom_fields) {
        if (!field.required) continue;
        const v = customFieldData[childId]?.[field.id];
        if (field.type === 'checkbox') { if (!Array.isArray(v) || v.length === 0) return false; }
        else if (!v || (typeof v === 'string' && !v.trim())) return false;
      }
    }
    return true;
  };

  // Price preview for a child at a given 1-based selection position. Include
  // this parent's existing completed enrollments so the preview matches the
  // backend/Stripe price when adding another child later.
  const priceForChild = (child, selectionPosition) => {
    const position = existingCompletedCount + selectionPosition;
    if (program.feature_per_role_pricing && program.price_map) {
      let best = program.price_map['default'] ?? program.price ?? 0;
      for (const role of child?.roles || []) {
        const rp = program.price_map[role.name];
        if (rp !== undefined && rp < best) best = rp;
      }
      return best;
    }
    let price = program.price || 0;
    if (program.feature_volume_discount && program.volume_discount > 0) {
      price = price - program.volume_discount * (position - 1);
    }
    if (program.minimum_price !== null && program.minimum_price !== undefined) {
      price = Math.max(price, program.minimum_price);
    }
    return price;
  };

  const total = () => selectedChildren.reduce((sum, childId, idx) => {
    const child = children.find((c) => c.id === childId);
    return sum + priceForChild(child, idx + 1);
  }, 0);

  const handleContinue = () => {
    if (selectedChildren.length === 0) { setError('Please select at least one child to enroll.'); return; }
    if (!validateCustom()) { setError('Please fill out all required fields for selected children.'); return; }
    const enrollments = selectedChildren.map((childId) => {
      const child = children.find((c) => c.id === childId);
      return {
        enrollee_id: childId,
        name: `${child.first_name} ${child.last_name}`,
        custom_responses: customFieldData[childId] || {}
      };
    });
    navigate(`/programs/${id}/rules`, {
      state: { programId: id, programName: program.name, programRules: program.rules, enrollments }
    });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>Loading...</Typography>
        </Box>
      </Box>
    );
  }

  if (error && !program) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/programs/${id}`)}>Back to Program</Button>
        </Container>
      </Box>
    );
  }
  if (!program) return null;

  const deadlinePassed = program.enrollment_deadline && new Date(program.enrollment_deadline) < new Date();
  const enrollBlocked = !program.enrollment_open || deadlinePassed;

  const renderField = (child, field) => {
    const val = customFieldData[child.id]?.[field.id] || '';
    if (field.type === 'select') {
      return (
        <TextField key={field.id} fullWidth select label={field.label} required={field.required}
          value={val} onChange={(e) => setCustom(child.id, field.id, e.target.value)}>
          <MenuItem value=""><em>Select an option</em></MenuItem>
          {(field.options || []).map((o, i) => <MenuItem key={i} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    }
    if (field.type === 'radio') {
      return (
        <Box key={field.id}>
          <Typography variant="body2" sx={{ mb: 1 }}>{field.label} {field.required && <span style={{ color: 'red' }}>*</span>}</Typography>
          {(field.options || []).map((o, i) => (
            <FormControlLabel key={i} control={<Checkbox checked={val === o} onChange={() => setCustom(child.id, field.id, o)} />} label={o} />
          ))}
        </Box>
      );
    }
    if (field.type === 'checkbox') {
      const checked = Array.isArray(val) ? val : [];
      return (
        <Box key={field.id}>
          <Typography variant="body2" sx={{ mb: 1 }}>{field.label} {field.required && <span style={{ color: 'red' }}>*</span>}</Typography>
          {(field.options || []).map((o, i) => (
            <FormControlLabel key={i} control={<Checkbox checked={checked.includes(o)}
              onChange={(e) => setCustom(child.id, field.id, e.target.checked ? [...checked, o] : checked.filter((v) => v !== o))} />} label={o} />
          ))}
        </Box>
      );
    }
    if (field.type === 'date') {
      return <TextField key={field.id} fullWidth type="date" label={field.label} required={field.required}
        value={val} onChange={(e) => setCustom(child.id, field.id, e.target.value)} InputLabelProps={{ shrink: true }} />;
    }
    return (
      <TextField key={field.id} fullWidth label={field.label} placeholder={field.placeholder} required={field.required}
        value={val} onChange={(e) => setCustom(child.id, field.id, e.target.value)}
        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
        multiline={field.type === 'textarea'} rows={field.type === 'textarea' ? 3 : 1} />
    );
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="md">
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/programs/${id}`)} sx={{ mb: 3 }}>
          Back to Program Details
        </Button>

        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>Enroll in {program.name}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>Select the children you'd like to enroll</Typography>

        {location.state?.childAdded && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => navigate(location.pathname, { replace: true, state: {} })}>
            Child added successfully. You can now select them below.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {deadlinePassed && <Alert severity="warning" sx={{ mb: 3 }}>The enrollment deadline has passed.</Alert>}
        {!program.enrollment_open && <Alert severity="info" sx={{ mb: 3 }}>Enrollment is currently closed.</Alert>}

        {children.length === 0 ? (
          <Card>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>No Children Added Yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Add your children to enroll them in programs.</Typography>
              <Button component={RouterLink} to="/children/add"
                state={{ returnTo: `/programs/${id}/enroll` }} variant="contained" startIcon={<PersonAddIcon />}>
                Add Your First Child
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Select Children</Typography>
                <Button component={RouterLink} to="/children/add"
                  state={{ returnTo: `/programs/${id}/enroll` }} variant="outlined" size="small"
                  startIcon={<PersonAddIcon />} disabled={children.length >= 10}>
                  Add Another Child
                </Button>
              </Box>
              <Stack spacing={2}>
                {children.map((child) => {
                  const eligible = isChildEligible(child);
                  const enrolled = enrolledChildIds.includes(child.id);
                  const paymentIncomplete = pendingChildIds.includes(child.id);
                  const selected = selectedChildren.includes(child.id);
                  const grade = child.child_properties?.grade;
                  return (
                    <Box key={child.id}>
                      <Paper sx={{ p: 2, border: '1px solid',
                        borderColor: enrolled ? 'success.main' : paymentIncomplete ? 'warning.main' : !eligible ? 'grey.400' : selected ? 'primary.main' : 'divider',
                        bgcolor: enrolled ? 'success.50' : paymentIncomplete ? 'warning.50' : selected ? 'primary.50' : 'background.paper' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{child.first_name} {child.last_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {grade !== null && grade !== undefined && `Grade ${grade}`}
                              {grade !== null && grade !== undefined && child.gender && ' • '}
                              {child.gender && child.gender.charAt(0).toUpperCase() + child.gender.slice(1)}
                            </Typography>
                            {paymentIncomplete && (
                              <Typography variant="caption" color="warning.dark">
                                Payment incomplete — select this child to retry checkout.
                              </Typography>
                            )}
                          </Box>
                          {enrolled ? <Chip label="Enrolled" color="success" variant="outlined" />
                            : !eligible ? <Chip label="Not Eligible" color="error" variant="outlined" />
                            : <Checkbox checked={selected} onChange={() => toggleChild(child.id)} />}
                        </Box>
                      </Paper>

                      {selected && hasCustomFields && (
                        <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                            Additional Information for {child.first_name}
                          </Typography>
                          <Stack spacing={2}>{program.custom_fields.map((f) => renderField(child, f))}</Stack>
                        </Paper>
                      )}
                    </Box>
                  );
                })}
              </Stack>

              {selectedChildren.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Enrollment Summary</Typography>
                  {existingCompletedCount > 0 && program.feature_volume_discount && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Pricing includes {existingCompletedCount} existing completed enrollment{existingCompletedCount !== 1 ? 's' : ''} for this program.
                    </Typography>
                  )}
                  <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 3 }}>
                    {selectedChildren.map((childId, idx) => {
                      const child = children.find((c) => c.id === childId);
                      return (
                        <Box key={childId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography>{child?.first_name} {child?.last_name}</Typography>
                          <Typography sx={{ fontWeight: 600 }}>${priceForChild(child, idx + 1).toFixed(2)}</Typography>
                        </Box>
                      );
                    })}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>Total</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>${total().toFixed(2)}</Typography>
                    </Box>
                  </Paper>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => setSelectedChildren([])}>Clear Selection</Button>
                    <Button variant="contained" onClick={handleContinue} disabled={enrollBlocked}>
                      {`Continue with ${selectedChildren.length} Child${selectedChildren.length !== 1 ? 'ren' : ''}`}
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
}

export default Enrollment;
